import { useEffect, useMemo, useState } from 'react'
import {
  approveResourceConnectionDefaultRules,
  createResourceConnection,
  createRuleAdmin,
  deleteResourceConnection,
  deleteRuleAdmin,
  fetchPluginInstalls,
  fetchResourceConnectionDefaultRules,
  fetchResourceConnections,
  fetchRulesAdmin,
  updateResourceConnection,
} from '../../api/ws'
import type { ManifestRuleWithStatus } from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { ConnectionConfigSchema, ResourceConnection, Rule } from '../../store/types'
import { InstallWizard } from './InstallWizard'
import { AccessGrantSheet } from './AccessGrantSheet'
import { ConnectionAccounts } from './ConnectionAccounts'

// Debug CRUD view for Resource Connections. List + add form + per-row
// edit (display_name and config) + inline rules management (scoped to
// the connection). Reachable via gear → About → Debug → "Resource
// Connections".
export function ResourceConnectionsSheet() {
  const connections = useAppStore((s) => s.resourceConnections)
  const installs = useAppStore((s) => s.pluginInstalls)
  const agents = useAppStore((s) => s.agentInstances)
  const currentOrg = useAppStore((s) => s.availableOrganizations.find((o) => o.is_current))

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [allRules, setAllRules] = useState<Rule[]>([])

  // Which row is expanded for edit / rules management.
  const [expandedID, setExpandedID] = useState<string | null>(null)

  // Surface B: the connection whose "who can use this?" sheet is open.
  const [accessForConn, setAccessForConn] = useState<ResourceConnection | null>(null)
  // Agents eligible to bind — same host as the connection (the plugin runs
  // there). Fall back to all live agents when the connection has no host_id.
  const accessAgents = useMemo(() => {
    const live = agents.filter((a) => !a.retired_at)
    if (!accessForConn?.host_id) return live
    return live.filter((a) => a.host_id === accessForConn.host_id)
  }, [agents, accessForConn])

  // Install-wizard state for approving manifest defaults.
  const [wizardForConnID, setWizardForConnID] = useState<string | null>(null)
  const [wizardDefaults, setWizardDefaults] = useState<ManifestRuleWithStatus[]>([])
  const [wizardLoading, setWizardLoading] = useState(false)

  useEffect(() => {
    fetchResourceConnections()
    fetchPluginInstalls()
    refreshRules()
  }, [])

  async function refreshRules() {
    const resp = await fetchRulesAdmin()
    setAllRules(resp.rules ?? [])
  }

  async function refresh() {
    fetchResourceConnections()
    fetchPluginInstalls()
    await refreshRules()
  }

  async function handleCreate(form: {
    slug: string
    pluginInstallID: string
    displayName: string
    config: string
  }) {
    setError(null)
    setBusy(true)
    try {
      const resp = await createResourceConnection({
        slug: form.slug.trim(),
        pluginInstallID: form.pluginInstallID,
        displayName: form.displayName.trim() || undefined,
        config: form.config,
      })
      if (resp?.error) {
        setError(resp.error)
        return
      }
      await refresh()
      // Open the rules wizard with the server-assigned UUID.
      if (resp.id) openWizard(resp.id)
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  async function handleUpdate(id: string, displayName: string, config: string) {
    setError(null)
    setBusy(true)
    try {
      const resp = await updateResourceConnection({ id, displayName, config })
      if (resp?.error) {
        setError(resp.error)
        return
      }
      await refresh()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete(id: string) {
    if (!confirm(`Delete connection "${id}"? Rules scoped to it will also be removed.`)) return
    setError(null)
    setBusy(true)
    try {
      const resp = await deleteResourceConnection(id)
      if (resp?.error) {
        setError(resp.error)
        return
      }
      if (expandedID === id) setExpandedID(null)
      await refresh()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  async function openWizard(connectionID: string) {
    setWizardForConnID(connectionID)
    setWizardDefaults([])
    setWizardLoading(true)
    try {
      const resp = await fetchResourceConnectionDefaultRules(connectionID)
      if (resp.error) {
        setError(resp.error)
        setWizardForConnID(null)
        return
      }
      setWizardDefaults(resp.manifest_defaults ?? [])
    } catch (e) {
      setError(String(e))
      setWizardForConnID(null)
    } finally {
      setWizardLoading(false)
    }
  }

  async function approveWizard(
    connectionID: string,
    rules: { action: string; decision: 'allow' | 'deny' | 'ask' }[],
  ) {
    setBusy(true)
    setError(null)
    try {
      const resp = await approveResourceConnectionDefaultRules({ resourceConnectionID: connectionID, rules })
      if (resp.error) {
        setError(resp.error)
        return
      }
      setWizardForConnID(null)
      setWizardDefaults([])
      await refreshRules()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  async function handleAddRule(
    connectionID: string,
    action: string,
    decision: string,
    predicate: string,
  ) {
    if (!currentOrg) return
    setError(null)
    setBusy(true)
    try {
      const resp = await createRuleAdmin({
        principalScopeKind: 'organization',
        principalScopeValue: currentOrg.id,
        resourceKind: 'external_resource',
        resourceID: connectionID,
        action: action.trim(),
        predicate: predicate.trim() || null,
        decision: decision,
      })
      if (resp.error) {
        setError(resp.error)
        return
      }
      await refreshRules()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  async function handleDeleteRule(ruleID: string) {
    if (!confirm(`Delete rule #${ruleID}?`)) return
    setError(null)
    setBusy(true)
    try {
      const resp = await deleteRuleAdmin(ruleID)
      if (resp.error) {
        setError(resp.error)
        return
      }
      await refreshRules()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ opacity: 0.65, marginTop: 0 }}>
        Each connection is a configured instance of a plugin — your specific Home Assistant server or your Google Calendar — that can be granted to agents.
      </p>

      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <h3 style={{ margin: '8px 0' }}>Connections</h3>
      {connections.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No connections yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {connections.map((c) => {
            const connRules = allRules.filter(
              (r) => r.resource_kind === 'external_resource' && r.resource_id === c.id,
            )
            const isExpanded = expandedID === c.id
            return (
              <ConnectionRow
                key={c.id}
                connection={c}
                installs={installs}
                rules={connRules}
                isExpanded={isExpanded}
                busy={busy}
                onToggleExpand={() => setExpandedID(isExpanded ? null : c.id)}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onOpenWizard={openWizard}
                onOpenAccess={() => setAccessForConn(c)}
                onAddRule={handleAddRule}
                onDeleteRule={handleDeleteRule}
              />
            )
          })}
        </div>
      )}

      {accessForConn && (
        <AccessGrantSheet
          connection={accessForConn}
          agents={accessAgents}
          onClose={() => setAccessForConn(null)}
          onDone={() => {
            setAccessForConn(null)
            refreshRules()
          }}
        />
      )}

      <h3 style={{ margin: '20px 0 8px' }}>Add connection</h3>
      <CreateForm installs={installs} disabled={busy} onSubmit={handleCreate} />

      {wizardForConnID && (
        <div
          style={{
            marginTop: 20,
            border: '1px solid #ddd',
            borderRadius: 4,
            padding: 12,
          }}
        >
          <h3 style={{ margin: '4px 0 8px' }}>
            Approve default rules for <code>{wizardForConnID}</code>
          </h3>
          {wizardLoading ? (
            <p style={{ opacity: 0.6 }}>Loading manifest…</p>
          ) : (
            <InstallWizard
              manifestDefaults={wizardDefaults}
              scopeLabel="for this connection (org-wide)"
              submitLabel="Approve for household"
              busy={busy}
              onApprove={(rules) => approveWizard(wizardForConnID, rules)}
              onCancel={() => {
                setWizardForConnID(null)
                setWizardDefaults([])
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

// Per-row component. Collapsed: one-liner with toggle + action buttons.
// Expanded: edit form for display_name/config + inline rules manager.
function ConnectionRow({
  connection,
  installs,
  rules,
  isExpanded,
  busy,
  onToggleExpand,
  onUpdate,
  onDelete,
  onOpenWizard,
  onOpenAccess,
  onAddRule,
  onDeleteRule,
}: {
  connection: ReturnType<typeof useAppStore.getState>['resourceConnections'][number]
  installs: ReturnType<typeof useAppStore.getState>['pluginInstalls']
  rules: Rule[]
  isExpanded: boolean
  busy: boolean
  onToggleExpand: () => void
  onUpdate: (id: string, displayName: string, config: string) => void
  onDelete: (id: string) => void
  onOpenWizard: (id: string) => void
  onOpenAccess: () => void
  onAddRule: (connID: string, action: string, decision: string, predicate: string) => void
  onDeleteRule: (ruleID: string) => void
}) {
  const install = installs.find((p) => p.id === connection.plugin_install_id)
  const schema = useMemo(() => parseConfigSchema(install?.config_schema), [install])

  // Edit form state — initialised from the live connection on each expand.
  const [editName, setEditName] = useState(connection.display_name ?? '')
  const [editConfig, setEditConfig] = useState<Record<string, string>>({})

  // Reset edit state when the row is expanded so we show current server values.
  useEffect(() => {
    if (isExpanded) {
      setEditName(connection.display_name ?? '')
      const parsed = parseConfigValues(connection.config, schema)
      setEditConfig(parsed)
    }
  }, [isExpanded, connection.display_name, connection.config, schema])

  function submitEdit(e: React.FormEvent) {
    e.preventDefault()
    const config = buildConfigJSON(schema, editConfig)
    onUpdate(connection.id, editName, config)
  }

  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '8px 10px',
          background: isExpanded ? '#f5f5f5' : 'white',
          cursor: 'pointer',
        }}
        onClick={onToggleExpand}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{connection.slug || connection.id}</div>
          <div style={{ opacity: 0.65, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {connection.plugin_slug ?? '?'} on {(connection.host_id ?? '').slice(0, 8)}…
            {connection.display_name ? ` — ${connection.display_name}` : null}
            {rules.length > 0 ? ` · ${rules.length} rule${rules.length !== 1 ? 's' : ''}` : null}
          </div>
        </div>
        <span style={{ opacity: 0.5, fontSize: 12 }}>{isExpanded ? '▲' : '▼'}</span>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div
          style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 16 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Edit form */}
          <section>
            <h4 style={{ margin: '0 0 8px', fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Edit</h4>
            <form onSubmit={submitEdit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, opacity: 0.7 }}>Display name</span>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="(optional)"
                  style={{ fontSize: 16 }}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </label>
              <ConfigFields schema={schema} values={editConfig} onChange={setEditConfig} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={busy}>Save</button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onOpenWizard(connection.id)}
                >
                  Configure rules…
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={onOpenAccess}
                >
                  Who can use this?…
                </button>
                <button
                  type="button"
                  disabled={busy}
                  style={{ marginLeft: 'auto', color: '#900' }}
                  onClick={() => onDelete(connection.id)}
                >
                  Delete
                </button>
              </div>
            </form>
          </section>

          {/* Accounts (identities): view "connected as X", revoke, or
              connect/re-authorize a fresh account on this connection. */}
          <ConnectionAccounts connection={connection} />

          {/* Rules */}
          <section>
            <h4 style={{ margin: '0 0 8px', fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Rules ({rules.length})
            </h4>
            {rules.length === 0 ? (
              <p style={{ margin: 0, opacity: 0.55, fontSize: 13 }}>No rules yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {rules.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      padding: '5px 8px',
                      background: '#fafafa',
                      borderRadius: 4,
                      border: '1px solid #eee',
                    }}
                  >
                    <div style={{ flex: 1, fontSize: 12, fontFamily: 'monospace' }}>
                      <span style={{ fontWeight: 600 }}>{r.action}</span>
                      {r.predicate ? <span style={{ opacity: 0.6 }}> · {r.predicate}</span> : null}
                    </div>
                    <DecisionBadge decision={r.decision} />
                    <span style={{ fontSize: 11, opacity: 0.45 }}>#{r.id}</span>
                    <button
                      style={{ fontSize: 11, color: '#900', padding: '2px 6px' }}
                      disabled={busy}
                      onClick={() => onDeleteRule(r.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <AddRuleForm
              connectionID={connection.id}
              disabled={busy}
              onAdd={onAddRule}
            />
          </section>
        </div>
      )}
    </div>
  )
}

function DecisionBadge({ decision }: { decision: string }) {
  const colors: Record<string, string> = { allow: '#070', deny: '#900', ask: '#660' }
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: 3,
        background: '#eee',
        color: colors[decision] ?? '#333',
        textTransform: 'uppercase',
      }}
    >
      {decision}
    </span>
  )
}

function AddRuleForm({
  connectionID,
  disabled,
  onAdd,
}: {
  connectionID: string
  disabled: boolean
  onAdd: (connID: string, action: string, decision: string, predicate: string) => void
}) {
  const [action, setAction] = useState('')
  const [decision, setDecision] = useState('allow')
  const [predicate, setPredicate] = useState('')
  const [open, setOpen] = useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!action.trim()) return
    onAdd(connectionID, action.trim(), decision, predicate.trim())
    setAction('')
    setPredicate('')
    setOpen(false)
  }

  if (!open) {
    return (
      <button
        style={{ marginTop: 8, fontSize: 13 }}
        onClick={() => setOpen(true)}
      >
        + Add rule
      </button>
    )
  }

  return (
    <form
      onSubmit={submit}
      style={{
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '8px 10px',
        background: '#f5f5f5',
        borderRadius: 4,
        border: '1px solid #ddd',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 11, opacity: 0.7 }}>Action (e.g. github:list_issues)</span>
        <input
          value={action}
          onChange={(e) => setAction(e.target.value)}
          style={{ fontSize: 14 }}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
        />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
          <span style={{ fontSize: 11, opacity: 0.7 }}>Decision</span>
          <select value={decision} onChange={(e) => setDecision(e.target.value)} style={{ fontSize: 14 }}>
            <option value="allow">allow</option>
            <option value="ask">ask</option>
            <option value="deny">deny</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 2 }}>
          <span style={{ fontSize: 11, opacity: 0.7 }}>Predicate (optional JSON)</span>
          <input
            value={predicate}
            onChange={(e) => setPredicate(e.target.value)}
            style={{ fontSize: 14 }}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={disabled || !action.trim()} style={{ fontSize: 13 }}>Add</button>
        <button type="button" onClick={() => setOpen(false)} style={{ fontSize: 13 }}>Cancel</button>
      </div>
    </form>
  )
}

function CreateForm({
  installs,
  disabled,
  onSubmit,
}: {
  installs: ReturnType<typeof useAppStore.getState>['pluginInstalls']
  disabled: boolean
  onSubmit: (form: {
    slug: string
    pluginInstallID: string
    displayName: string
    config: string
  }) => void
}) {
  const [slug, setSlug] = useState('')
  const [picked, setPicked] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [configValues, setConfigValues] = useState<Record<string, string>>({})

  const pickedInstall = useMemo(() => installs.find((p) => p.id === picked) ?? null, [installs, picked])
  const schema = useMemo(() => parseConfigSchema(pickedInstall?.config_schema), [pickedInstall])

  function pickInstall(newID: string) {
    setPicked(newID)
    const next: Record<string, string> = {}
    const newPick = installs.find((p) => p.id === newID) ?? null
    const newSchema = parseConfigSchema(newPick?.config_schema)
    if (newSchema?.properties) {
      for (const [key, field] of Object.entries(newSchema.properties)) {
        if (field.default !== undefined) next[key] = String(field.default)
      }
    }
    setConfigValues(next)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!slug.trim() || !picked) return
    const config = buildConfigJSON(schema, configValues)
    onSubmit({ slug, pluginInstallID: picked, displayName, config })
    setSlug('')
    setDisplayName('')
    setConfigValues({})
  }

  if (installs.length === 0) {
    return (
      <p style={{ opacity: 0.6 }}>
        No plugins reported by any host yet. Install one under ~/.hearth/plugins/ and restart the daemon.
      </p>
    )
  }

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Slug</span>
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="e.g. echo_test"
          style={{ fontSize: 16 }}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Plugin install</span>
        <select value={picked} onChange={(e) => pickInstall(e.target.value)} style={{ fontSize: 16 }}>
          <option value="">Select…</option>
          {installs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.display_name || p.plugin_slug} on {p.host_id.slice(0, 8)}…
            </option>
          ))}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Display name (optional)</span>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          style={{ fontSize: 16 }}
        />
      </label>
      <ConfigFields schema={schema} values={configValues} onChange={setConfigValues} />
      <button
        type="submit"
        disabled={disabled || !slug.trim() || !picked}
        style={{ marginTop: 4, padding: '8px 12px' }}
      >
        Create
      </button>
    </form>
  )
}

function ConfigFields({
  schema,
  values,
  onChange,
}: {
  schema: ConnectionConfigSchema | null
  values: Record<string, string>
  onChange: (next: Record<string, string>) => void
}) {
  if (!schema?.properties || Object.keys(schema.properties).length === 0) return null
  const required = new Set(schema.required ?? [])
  return (
    <fieldset style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12, margin: 0 }}>
      <legend style={{ fontSize: 12, opacity: 0.7, padding: '0 6px' }}>Plugin config</legend>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Object.entries(schema.properties).map(([key, field]) => (
          <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              {key}
              {required.has(key) ? <span style={{ color: '#900' }}> *</span> : null}
              {field.description ? <span style={{ opacity: 0.5, marginLeft: 6 }}>— {field.description}</span> : null}
            </span>
            {field.enum ? (
              <select
                value={values[key] ?? ''}
                onChange={(e) => onChange({ ...values, [key]: e.target.value })}
                style={{ fontSize: 16 }}
              >
                <option value="">Select…</option>
                {field.enum.map((v) => (
                  <option key={String(v)} value={String(v)}>{String(v)}</option>
                ))}
              </select>
            ) : field.type === 'boolean' ? (
              <input
                type="checkbox"
                checked={values[key] === 'true'}
                onChange={(e) => onChange({ ...values, [key]: e.target.checked ? 'true' : 'false' })}
                style={{ alignSelf: 'flex-start' }}
              />
            ) : (
              <input
                type={field.type === 'integer' || field.type === 'number' ? 'number' : 'text'}
                value={values[key] ?? ''}
                onChange={(e) => onChange({ ...values, [key]: e.target.value })}
                style={{ fontSize: 16 }}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
              />
            )}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function parseConfigSchema(raw: string | undefined): ConnectionConfigSchema | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as ConnectionConfigSchema
  } catch {
    return null
  }
}

// parseConfigValues inflates a stored JSON config string into the
// flat string-keyed map used by ConfigFields. Unknown keys (not in
// schema) are ignored; missing keys get empty string.
function parseConfigValues(
  config: string | undefined,
  schema: ConnectionConfigSchema | null,
): Record<string, string> {
  if (!schema?.properties) return {}
  let parsed: Record<string, unknown> = {}
  try {
    if (config && config !== '{}') parsed = JSON.parse(config)
  } catch {
    // malformed stored config — start blank
  }
  const out: Record<string, string> = {}
  for (const key of Object.keys(schema.properties)) {
    const v = parsed[key]
    out[key] = v !== undefined ? String(v) : ''
  }
  return out
}

function buildConfigJSON(schema: ConnectionConfigSchema | null, values: Record<string, string>): string {
  if (!schema?.properties) return '{}'
  const out: Record<string, unknown> = {}
  for (const [key, field] of Object.entries(schema.properties)) {
    const raw = values[key]
    if (raw === undefined || raw === '') continue
    switch (field.type) {
      case 'integer': {
        const n = parseInt(raw, 10)
        if (!Number.isNaN(n)) out[key] = n
        break
      }
      case 'number': {
        const n = parseFloat(raw)
        if (!Number.isNaN(n)) out[key] = n
        break
      }
      case 'boolean':
        out[key] = raw === 'true'
        break
      default:
        out[key] = raw
    }
  }
  return JSON.stringify(out)
}
